import React, { Fragment, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import {
  useTable,
  useGlobalFilter,
  useAsyncDebounce,
  useSortBy,
  useFilters,
  useExpanded,
  usePagination,
  useRowSelect,
} from "react-table";
import { Table, Row, Col, Button, Input } from "reactstrap";
import { Filter, DefaultColumnFilter } from "./filters";
import { Label } from "recharts";
import Select from "react-select";

// Define a default UI for filtering
function GlobalFilter({
  preGlobalFilteredRows,
  globalFilter,
  setGlobalFilter,
}) {
  const count = preGlobalFilteredRows.length;
  const [value, setValue] = React.useState(globalFilter);
  const onChange = useAsyncDebounce(value => {
    setGlobalFilter(value || undefined);
  }, 200);

  return (
    <Col md={3} className="table-search text-sm-center text-md-end">
      <div className="search-box me-3 mb-0 d-inline-block">
        <div className="position-relative">
          <label htmlFor="search-bar-0" className="search-label m-0">
            <span id="search-bar-0-label" className="sr-only">
              Search this table
            </span>
            <input
              onChange={e => {
                setValue(e.target.value);
                onChange(e.target.value);
              }}
              id="search-bar-0"
              type="text"
              className="form-control"
              placeholder={`Search records...`} 
              value={value || ""}
            />
          </label>
          <i className="bx bx-search-alt search-icon"></i>
        </div>
      </div>
    </Col>
  );
}

const TableContainer = ({
  columns,
  data,
  isGlobalFilter,
  isAddOptions,
  isAddUserList,
  isAddApiProject,
  isAddRole,
  isAddCategoryList,
  isAddModuleList,
  handleOrderClicks,
  handleUserClick,
  handleCatClick,
  handleModuleClick,
  handleCustomerClick,
  isAddCustList,
  customPageSize,
  className,
  customPageSizeOptions,
  isAddPayoutRequest,
  isSelectAllTableRow,
  handleOnSwitchChange,
  isAddAPIProviderSearch,
  handleSelectGroup,
  handleInputChange,
  optionGroup,
  selectedGroup,
  isTyping,
  tableSorter = true,
  showFooter = false,
  onSelectedRowsChange,
}) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
     footerGroups, 
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state,
    preGlobalFilteredRows,
    setGlobalFilter,
    selectedFlatRows,
    state: { selectedRowIds, pageIndex, pageSize }
  } = useTable(
    {
      columns,
      data,
      defaultColumn: { Filter: DefaultColumnFilter },
      initialState: {
        pageIndex: 0,
        pageSize: customPageSize,
        sortBy: [
          {
            desc: true,
          },
        ],
      },
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect,
  );
  const prevSelectedRowsRef = React.useRef([]);
  const selectedOriginalRows = useMemo(() => {
    return selectedFlatRows.map(row => row.original);
  }, [selectedFlatRows]);

  useEffect(() => {
    // Check if new selected rows are different than previous
    const isEqual = (a, b) => a.length === b.length && a.every((v, i) => v === b[i]);

    if (onSelectedRowsChange && !isEqual(prevSelectedRowsRef.current, selectedOriginalRows)) {
      prevSelectedRowsRef.current = selectedOriginalRows;
      onSelectedRowsChange(selectedOriginalRows);
    }
  }, [selectedOriginalRows, onSelectedRowsChange]);

  const generateSortingIndicator = column => {
    if (!column.canSort) return null;
    return (
      <span style={{ marginLeft: 4, display: "inline-flex", flexDirection: "column", lineHeight: 1, verticalAlign: "middle" }}>
        <i className="mdi mdi-chevron-up" style={{ fontSize: 12, opacity: column.isSorted && !column.isSortedDesc ? 1 : 0.25, lineHeight: 1 }} />
        <i className="mdi mdi-chevron-down" style={{ fontSize: 12, opacity: column.isSorted && column.isSortedDesc ? 1 : 0.25, lineHeight: 1 }} />
      </span>
    );
  };

  const onChangeInSelect = event => {
    setPageSize(Number(event.target.value));
  };

  const onChangeRequestStatus = event => {
    setGlobalFilter(event.target.value || undefined);
  };

  const onChangeInInput = event => {
    const page = event.target.value ? Number(event.target.value) - 1 : 0;
    gotoPage(page);
  };
  return (
    <Fragment>
      {data.length === 0 ? (
        <div className="text-center text-muted py-4">
          <i className="fas fa-info-circle fa-2x mb-2" />
          <p className="mb-0">No Records Found</p>
        </div>
      ) : (
        <>
          <Row className="mb-2 g-2 responsive-table-row">
            {tableSorter && (
              <Col md={2} sm={3} className="">
                <select
                  className="form-select"
                  value={pageSize}
                  onChange={onChangeInSelect}
                >
                  {[10, 20, 25, 50, 75, 100].map(pageSize => (
                    <option key={pageSize} value={pageSize}>
                      Show {pageSize}
                    </option>
                  ))}
                </select>
              </Col>
            )}
            {isAddAPIProviderSearch && (
              <Col md="6">
                <div className="mb-3">
                  <Label htmlFor="formrow-apiprovider-Input">API Provider</Label>
                  <Select
                    value={selectedGroup}
                    onChange={handleSelectGroup}
                    onInputChange={handleInputChange}
                    options={optionGroup}
                    isSearchable={true}
                    clearable={true}
                    isLoading={isTyping}
                    placeholder="Search Provider Name..."
                  />
                </div>
              </Col>
            )
            }
            {isSelectAllTableRow && data.length > 0 && (
              <Col>
                <div className="form-check form-switch form-switch-lg mb-3" >
                  <input
                    style={{ marginLeft: '50px' }}
                    type="checkbox"
                    className="form-check-input"
                    id="table_customSwitch"
                    onChange={handleOnSwitchChange}
                  />
                  <label
                    className="form-check-label"
                    htmlFor="customSwitch2"
                  >
                    Select All
                  </label>
                </div>
              </Col>
            )}

            {/* <GlobalFilter
            preGlobalFilteredRows={preGlobalFilteredRows}
            globalFilter={state.globalFilter}
            setGlobalFilter={setGlobalFilter}
          /> */}

            {isGlobalFilter && (
              <GlobalFilter
                preGlobalFilteredRows={preGlobalFilteredRows}
                globalFilter={state.globalFilter}
                setGlobalFilter={setGlobalFilter}
              />
            )}
            {isAddPayoutRequest && (
              <Col md="2">
                <select
                  className="form-select"
                  onChange={onChangeRequestStatus}
                  placeholder="Request Status"
                >
                  <option value="">Request Status</option>
                  {isAddPayoutRequest.map(status => (
                    <option key={status.value} value={status.label}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </Col>
            )}
            {isAddOptions && (
              <Col sm="7">
                <div className="text-sm-end">
                  <Button
                    type="button"
                    color="success"
                    className="btn-rounded  mb-2 me-2"
                    onClick={handleOrderClicks}
                  >
                    <i className="mdi mdi-plus me-1" />
                    Add payout Request
                  </Button>
                </div>
              </Col>
            )}
            {isAddUserList && (
              <Col sm="7">
                <div className="text-sm-end">
                  <Button
                    type="button"
                    color="primary"
                    className="btn mb-2 me-2"
                    onClick={handleUserClick}
                  >
                    <i className="mdi mdi-plus-circle-outline me-1" />
                    Create New User
                  </Button>
                </div>
              </Col>
            )}
            {isAddApiProject && (
              <Col sm="7">
                <div className="text-sm-end">
                  <Button
                    type="button"
                    color="primary"
                    className="btn mb-2 me-2"
                    onClick={handleUserClick}
                  >
                    <i className="mdi mdi-plus-circle-outline me-1" />
                    Create Api Project
                  </Button>
                </div>
              </Col>
            )}
            {isAddRole && (
              <Col sm="7">
                <div className="text-sm-center text-md-start">
                  <Button
                    type="button"
                    color="primary"
                    className="btn mb-2 me-2"
                    onClick={handleUserClick}
                  >
                    <i className="mdi mdi-plus-circle-outline me-1" />
                    Create Role
                  </Button>
                </div>
              </Col>
            )}
            {isAddCategoryList && (
              <Col sm="7">
                <div className="text-sm-end">
                  <Button
                    type="button"
                    color="primary"
                    className="btn mb-2 me-2"
                    onClick={handleCatClick}
                  >
                    <i className="mdi mdi-plus-circle-outline me-1" />
                    Create New Category
                  </Button>
                </div>
              </Col>
            )}
            {isAddModuleList && (
              <Col sm="7">
                <div className="text-sm-end">
                  <Button
                    type="button"
                    color="primary"
                    className="btn mb-2 me-2"
                    onClick={handleModuleClick}
                  >
                    <i className="mdi mdi-plus-circle-outline me-1" />
                    Create New Module
                  </Button>
                </div>
              </Col>
            )}
            {isAddCustList && (
              <Col sm="7">
                <div className="text-sm-end">
                  <Button
                    type="button"
                    color="success"
                    className="btn-rounded mb-2 me-2"
                    onClick={handleCustomerClick}
                  >
                    <i className="mdi mdi-plus me-1" />
                    New Customers
                  </Button>
                </div>
              </Col>
            )}
          </Row>

          <div className="table-responsive react-table">
            <Table bordered hover {...getTableProps()} className={className}>
              {/* <thead className="table-light table-nowrap">
            {headerGroups.map(headerGroup => (
              <tr key={headerGroup.id} {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => (
                  <th key={column.id}{...column.getHeaderProps()}
                  className={column.className || ""}>
                    <div className="" {...column.getSortByToggleProps()}>
                      {column.render("Header")}
                      {generateSortingIndicator(column)}
                    </div>
                    <Filter column={column} />
                  </th>
                ))}
              </tr>
            ))}
          </thead> */}

              <thead className="table-light table-nowrap">
                {headerGroups.map(headerGroup => {
                  const { key: headerGroupKey, ...restHeaderGroupProps } = headerGroup.getHeaderGroupProps();

                  return (
                    <tr key={headerGroupKey} {...restHeaderGroupProps}>
                      {headerGroup.headers.map(column => {
                        const { key: columnKey, ...restHeaderProps } = column.getHeaderProps();

                        return (
                          <th key={columnKey} {...restHeaderProps} className={column.className || ""}>
                            <div {...column.getSortByToggleProps()}>
                              {column.render("Header")}
                              {generateSortingIndicator(column)}
                            </div>
                            <Filter column={column} />
                          </th>
                        );
                      })}
                    </tr>
                  );
                })}
              </thead>

              <tbody {...getTableBodyProps()}>
                {page.map(row => {
                  prepareRow(row);
                  const rowProps = row.getRowProps();
                  const { key: rowKey, ...restRowProps } = rowProps;
                  return (
                    // <Fragment key={row.getRowProps().key}>
                    //    <tr>

                    //     {row.cells.map(cell => {
                    //       return (
                    //         <td key={cell.id} {...cell.getCellProps()}>
                    //           {cell.render("Cell")}
                    //         </td>
                    //       );

                    //     })

                    //    }

                    //   </tr>

                    // </Fragment>
                    <Fragment key={rowKey}>
                      <tr {...restRowProps}>
                        {row.cells.map(cell => {
                          const cellProps = cell.getCellProps({
                            style: cell.column.cellStyle || {},
                            className: cell.column.className || "",
                          });

                          const { key: cellKey, ...restCellProps } = cellProps;

                          return (
                            <Fragment key={cellKey}>
                              <td {...restCellProps}>
                                {cell.render("Cell")}
                              </td>
                            </Fragment>
                          );
                        })}
                      </tr>
                    </Fragment>
                  );
                })}
              </tbody>
                {showFooter && footerGroups?.length > 0 && (
                  <tfoot>
                    {footerGroups.map(footerGroup => {
                      const { key: footerGroupKey, ...restFooterGroupProps } =
                        footerGroup.getFooterGroupProps();

                      return (
                        <tr key={footerGroupKey} {...restFooterGroupProps}>
                          {footerGroup.headers.map(column => {
                            const { key: footerKey, ...restFooterProps } =
                              column.getFooterProps();

                            return (
                              <td
                                key={footerKey}
                                {...restFooterProps}
                                className="footer-cell"
                              >
                                {column.render("Footer")}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tfoot>
                )}
            </Table>
          </div>
          <Row className="align-items-center table-pagi gy-2">
            {/* LEFT */}
            <Col className="text-start text-muted">
              Showing{" "}
              <strong>{page.length > 0 ? pageIndex * pageSize + 1 : 0}</strong>{" "}
              to{" "}
              <strong>{pageIndex * pageSize + page.length}</strong>{" "}
              of <strong>{data.length}</strong> entries
            </Col>

            {/* RIGHT (your existing code, untouched) */}
            <Col className="col-md-auto col-auto">
              <div className="d-flex gap-1">
                <Button color="primary" onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
                  {"<<"}
                </Button>
                <Button color="primary" onClick={previousPage} disabled={!canPreviousPage}>
                  {"<"}
                </Button>
              </div>
            </Col>

            <Col className="col-md-auto d-none d-md-block col-auto">
              Page <strong>{pageIndex + 1} of {pageOptions.length}</strong>
            </Col>

            <Col className="col-md-auto col-auto">
              <Input
                type="number"
                min={1}
                style={{ width: 70 }}
                max={pageOptions.length}
                defaultValue={pageIndex + 1}
                onChange={onChangeInInput}
              />
            </Col>

            <Col className="col-md-auto col-auto">
              <div className="d-flex gap-1">
                <Button color="primary" onClick={nextPage} disabled={!canNextPage}>
                  {">"}
                </Button>
                <Button
                  color="primary"
                  onClick={() => gotoPage(pageCount - 1)}
                  disabled={!canNextPage}
                >
                  {">>"}
                </Button>
              </div>
            </Col>
          </Row>

        </>
      )}
    </Fragment>
  );
};

TableContainer.propTypes = {
  preGlobalFilteredRows: PropTypes.any,
};

export default TableContainer;